import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const featuredResources = [
  {
    category: "Trading Education",
    title: "Understanding Futures Markets: A Beginner's Guide for Diaspora Investors",
    excerpt: "A practical introduction to futures markets, trading discipline, and the education-first mindset behind Harambee's planned learning programs.",
    format: "Guide",
    availability: "Available Now",
    img: "/assets/images/site/placeholder-temp.svg",
  },
  {
    category: "Wealth Strategy",
    title: "Cross-Border Wealth Planning for the African Diaspora",
    excerpt: "Key planning considerations for families and professionals managing opportunity, capital, and long-term goals across multiple jurisdictions.",
    format: "Article",
    availability: "Available Now",
    img: "/assets/images/site/placeholder-temp.svg",
  },
  {
    category: "Nairobi Markets",
    title: "Why East Africa's Capital Markets Are Attracting Global Diaspora Capital",
    excerpt: "An overview of why Nairobi and the wider region remain central to Harambee's business-plan focus and long-term cross-border strategy.",
    format: "Market Brief",
    availability: "Available Now",
    img: "/assets/images/site/placeholder-temp.svg",
  },
];

const resourceTracks = [
  {
    icon: "ri-live-line",
    title: "Webinars & Live Sessions",
    desc: "Live digital programming focused on investor readiness, market literacy, and diaspora-centered wealth-building conversations.",
  },
  {
    icon: "ri-book-open-line",
    title: "Ebooks & Guides",
    desc: "Downloadable learning materials designed to make cross-border investing, futures education, and strategic planning more approachable.",
  },
  {
    icon: "ri-video-line",
    title: "Video Learning Library",
    desc: "Structured video resources intended to support self-paced learning across trading education, financial literacy, and long-term planning.",
  },
  {
    icon: "ri-team-line",
    title: "Investor-Readiness Cohorts",
    desc: "Education-first cohort experiences that help learners build confidence before moving into more advanced advisory pathways.",
  },
];

const upcomingItems = [
  "Digital learning resources aligned with the Trading Academy curriculum",
  "Investor-readiness webinar series for diaspora professionals and families",
  "Structured workshop programming tied to cross-border wealth-building themes",
  "Expanded educational content to support Harambee's phased go-to-market roadmap",
];

export default function InsightsPage() {
  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <Navbar />

      <section className="relative h-[60vh] min-h-[480px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/images/site/group1.jpg"
            alt="Harambee insights and resources"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#0D1F14]/75" />
        </div>
        <div className="relative z-10 text-center px-6">
          <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">
            Insights & Resources
          </span>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-bold text-white mb-4">
            Digital Learning for Investor Readiness
          </h1>
          <p className="text-white/65 text-lg max-w-3xl mx-auto font-light leading-relaxed">
            The Harambee business plan identifies webinars, ebooks, workshops, and digital learning resources as a core part of the platform. This page serves as the public hub for that education-first offering.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 bg-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                Featured Reading
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F]">
                Knowledge Hub
              </h2>
            </div>
            <p className="text-[#6B6B6B] text-base leading-relaxed max-w-md">
              Education is the first step in Harambee&apos;s phased model: build trust, deepen readiness, then guide qualified participants into more advanced pathways.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredResources.map((resource) => (
              <article key={resource.title} className="bg-[#F8F6F3] border border-[#E8E4DC] rounded-lg overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={resource.img}
                    alt={resource.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-7">
                  <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                    {resource.category}
                  </span>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2B2D2F] mb-3 leading-snug">
                    {resource.title}
                  </h3>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed mb-5">
                    {resource.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#9B9B9B]">
                    <span>{resource.format}</span>
                    <span>&bull;</span>
                    <span>{resource.availability}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-[#F8F6F3]">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-[#C9A961] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
              <span className="w-8 h-px bg-[#C9A961]" />
              Resource Tracks
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F] leading-tight mb-6">
              Built Around the Business Plan&apos;s Education Model
            </h2>
            <p className="text-[#6B6B6B] text-base leading-relaxed">
              The business plan positions digital learning as a core entry point into the Harambee ecosystem. These resource tracks reflect that intent and create a clear public destination for educational content that was already referenced across the site.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {resourceTracks.map((track) => (
              <div key={track.title} className="bg-white border border-[#E8E4DC] rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 mb-5">
                  <i className={`${track.icon} text-[#1A4D3C] text-xl`} />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2B2D2F] mb-3">
                  {track.title}
                </h3>
                <p className="text-[#6B6B6B] text-sm leading-relaxed">
                  {track.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img
              src="/assets/images/site/placeholder-temp.svg"
              alt="Harambee digital learning resources"
              className="w-full h-[520px] object-cover object-top rounded-lg"
            />
            <div className="absolute -bottom-6 -left-6 bg-[#C9A961] rounded-lg p-6">
              <div className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#2B2D2F]">4</div>
              <div className="text-[#2B2D2F] text-xs font-semibold uppercase tracking-wider mt-1">Planned Resource Tracks</div>
            </div>
          </div>

          <div>
            <span className="inline-flex items-center gap-2 text-[#C9A961] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
              <span className="w-8 h-px bg-[#C9A961]" />
              What&apos;s Coming
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F] leading-tight mb-6">
              Planned Digital Expansion
            </h2>
            <ul className="space-y-4">
              {upcomingItems.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#2B2D2F]">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-line text-[#1A4D3C] text-base" />
                  </div>
                  <span className="text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-lg border border-[#E8E4DC] bg-[#F8F6F3] p-5">
              <p className="text-[#6B6B6B] text-sm leading-relaxed">
                Resource availability will expand over time as Harambee executes its phased roadmap. Materials on this page are educational in nature and do not constitute investment, legal, or accounting advice.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
